import { createTheme, Theme } from "@mui/material/styles";

const sharedTypography = {
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  h1: {
    fontSize: "2.5rem",
    fontWeight: 700,
    letterSpacing: "-0.02em",
  },
  h2: {
    fontSize: "2rem",
    fontWeight: 600,
    letterSpacing: "-0.01em",
  },
  h3: {
    fontSize: "1.5rem",
    fontWeight: 600,
  },
  h4: {
    fontSize: "1.25rem",
    fontWeight: 600,
  },
  h5: {
    fontSize: "1rem",
    fontWeight: 600,
  },
  h6: {
    fontSize: "0.875rem",
    fontWeight: 600,
  },
  body1: {
    fontSize: "1rem",
    lineHeight: 1.6,
  },
  body2: {
    fontSize: "0.875rem",
    lineHeight: 1.5,
  },
  button: {
    textTransform: "none" as const,
    fontWeight: 600,
  },
};

const sharedShape = {
  borderRadius: 12,
};

const sharedComponents = (borderColor: string) => ({
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        padding: "10px 20px",
        fontSize: "0.9rem",
      },
      contained: {
        boxShadow: "none",
        "&:hover": {
          boxShadow: "0 4px 12px rgba(16, 185, 129, 0.3)",
        },
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        backgroundImage: "none",
        borderRadius: 16,
        border: `1px solid ${borderColor}`,
        boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        backgroundImage: "none",
      },
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        "& .MuiOutlinedInput-root": {
          borderRadius: 8,
        },
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: 6,
      },
    },
  },
});

export const lightTheme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#10b981",
      light: "#34d399",
      dark: "#059669",
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#6b7280",
      light: "#9ca3af",
      dark: "#4b5563",
    },
    success: {
      main: "#10b981",
      light: "#34d399",
      dark: "#059669",
    },
    error: {
      main: "#ef4444",
      light: "#f87171",
      dark: "#dc2626",
    },
    warning: {
      main: "#f59e0b",
      light: "#fbbf24",
      dark: "#d97706",
    },
    background: {
      default: "#f9fafb",
      paper: "#ffffff",
    },
    text: {
      primary: "#111827",
      secondary: "#6b7280",
    },
    divider: "#e5e7eb",
  },
  typography: sharedTypography,
  shape: sharedShape,
  components: sharedComponents("#e5e7eb"),
});

export const darkTheme = createTheme({
  palette: {
    mode: "dark",
    primary: {
      main: "#10b981",
      light: "#34d399",
      dark: "#059669",
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#6b7280",
      light: "#9ca3af",
      dark: "#4b5563",
    },
    success: {
      main: "#10b981",
      light: "#34d399",
      dark: "#059669",
    },
    error: {
      main: "#ef4444",
      light: "#f87171",
      dark: "#dc2626",
    },
    warning: {
      main: "#f59e0b",
      light: "#fbbf24",
      dark: "#d97706",
    },
    background: {
      default: "#111827",
      paper: "#1f2937",
    },
    text: {
      primary: "#f9fafb",
      secondary: "#9ca3af",
    },
    divider: "#374151",
  },
  typography: sharedTypography,
  shape: sharedShape,
  components: sharedComponents("#374151"),
});

export function getTheme(mode: "light" | "dark"): Theme {
  return mode === "dark" ? darkTheme : lightTheme;
}

// Keep backward compat default export
export const theme = lightTheme;
